"use client";

// React Imports
import {useEffect, useRef} from "react";
import type {CSSProperties} from "react";

// Third-party Imports
import styled from "@emotion/styled";

// Redux Imports
import {useSelector} from "react-redux";

// Type Imports
import type {VerticalNavContextProps} from "@menu/contexts/verticalNavContext";

// Component Imports
// import MaterializeLogo from "@core/svg/Logo";

// Config Imports
import themeConfig from "@configs/themeConfig";

// Hook Imports
import useVerticalNav from "@menu/hooks/useVerticalNav";
import {useSettings} from "@core/hooks/useSettings";

type LogoTextProps = {
  isHovered?: VerticalNavContextProps["isHovered"];
  isCollapsed?: VerticalNavContextProps["isCollapsed"];
  transitionDuration?: VerticalNavContextProps["transitionDuration"];
  isBreakpointReached?: VerticalNavContextProps["isBreakpointReached"];
  color?: CSSProperties["color"];
};

const LogoText = styled.span<LogoTextProps>`
  font-size: 1.25rem;
  line-height: 1.2;
  font-weight: 600;
  letter-spacing: 0.15px;
  text-transform: capitalize;
  color: var(--mui-palette-text-primary);
  color: ${({color}) => color ?? "var(--mui-palette-text-primary)"};
  transition: ${({transitionDuration}) => `margin-inline-start ${transitionDuration}ms ease-in-out, opacity ${transitionDuration}ms ease-in-out`};

  ${({isHovered, isCollapsed, isBreakpointReached}) =>
    !isBreakpointReached && isCollapsed && !isHovered ? "opacity: 0; margin-inline-start: 0;" : "opacity: 1; margin-inline-start: 8px;"}
`;

const Logo = ({color}: {color?: CSSProperties["color"]}) => {
  // Refs
  const logoTextRef = useRef<HTMLSpanElement>(null);

  // Hooks
  const {isHovered, transitionDuration, isBreakpointReached} = useVerticalNav();

  const {settings} = useSettings();

  // Get custom logo, logoText, and entity name from Redux
  const {logo, logoText, name: entityName} = useSelector((state: any) => state.entitySettingsReducer);

  // Vars
  const {layout} = settings;
  const hasCustomLogo = !!logo;
  const hasLogoText = !!logoText;

  const logoUrl = hasCustomLogo
    ? `${process.env.NEXT_PUBLIC_CLOUDFLARE_IMG_URL}/${logo}/public`
    : process.env.NEXT_PUBLIC_LOGO_URL || "/images/logos/edupop.png";

  // Determine what text to show
  // Default: show app name (templateName)
  // If logoText is set: show logoText (entity can customize)
  const displayText = logoText || themeConfig.templateName;
  const showText = hasCustomLogo ? hasLogoText : true; // Show text with custom logo only if logoText exists

  useEffect(() => {
    if (layout !== "collapsed") {
      return;
    }

    if (logoTextRef && logoTextRef.current) {
      if (!isBreakpointReached && layout === "collapsed" && !isHovered) {
        logoTextRef.current?.classList.add("hidden");
      } else {
        logoTextRef.current.classList.remove("hidden");
      }
    }
  }, [isHovered, layout, isBreakpointReached]);

  return (
    <div className='flex items-center min-bs-[24px]'>
      {/* <MaterializeLogo /> */}
      <img
        src={logoUrl}
        alt='logo'
        className={hasCustomLogo ? "h-12 max-w-full" : "h-5"}
        style={hasCustomLogo ? {objectFit: "contain", maxHeight: "48px", backgroundColor: "transparent", borderRadius: "5px"} : undefined}
      />
      {showText && (
        <LogoText
          color={color}
          ref={logoTextRef}
          isHovered={isHovered}
          isCollapsed={layout === "collapsed"}
          transitionDuration={transitionDuration}
          isBreakpointReached={isBreakpointReached}>
          {displayText}
        </LogoText>
      )}
    </div>
  );
};

export default Logo;
